// src/components/personal/sections/OrdersSection/components/Filters.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderFilters } from '../types/types';
import { getOrderStatusOptions, getSalesChannelOptions, getPaymentStatusOptions } from '../utils/statusHelpers';
import { TEXT_STYLES } from '@/theme/typography';
import { COMPONENT_STYLES } from '@/theme';
import { Card, Grid, Flex } from '@/components/ui/Layout';
import { Loader } from 'lucide-react';
import { getEmptyFilters } from '../utils/orderFilters';

interface FiltersProps {
  filters: OrderFilters;
  onChange: (filters: OrderFilters) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onReset?: () => void;
  onRefresh?: () => void;
}

const Filters: React.FC<FiltersProps> = ({ 
  filters, 
  onChange,
  disabled = false,
  isLoading = false,
  onReset,
  onRefresh
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Локальное состояние для полей формы
  const [localFilters, setLocalFilters] = useState<OrderFilters>(filters);
  const [isApplying, setIsApplying] = useState(false);
  
  // Обновляем локальные фильтры при изменении внешних
  useEffect(() => {
    if (!isApplying) {
      setLocalFilters(filters);
    }
  }, [filters, isApplying]);
  
  // Обработчик изменения фильтров
  const handleLocalChange = (field: keyof OrderFilters, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Функция для создания точного совпадения
  const setExactMatch = (field: 'amountFrom' | 'amountTo') => {
    if (field === 'amountFrom' && localFilters.amountFrom) {
      setLocalFilters(prev => ({
        ...prev,
        amountTo: prev.amountFrom
      }));
    }
  };
  
  // Обработчик применения фильтров
  const handleApplyFilters = () => {
    if (DEBUG) console.log('Filters - Применение фильтров:', localFilters);
    
    setIsApplying(true);
    
    try {
      // Передаем фильтры в родительский компонент
      onChange(localFilters);
      
      // Вызываем функцию обновления данных после применения фильтров
      if (onRefresh) {
        console.log('Запуск обновления данных после применения фильтров');
        onRefresh();
      }
    } finally {
      // Добавляем задержку для визуального эффекта
      setTimeout(() => {
        setIsApplying(false);
      }, 500);
    }
  };
  
  // Обработчик сброса фильтров
  const handleReset = () => {
    if (DEBUG) console.log('Filters - Сброс фильтров');
    
    const resetFilters = getEmptyFilters();
    setLocalFilters(resetFilters);
    
    if (onReset) {
      onReset();
    } else {
      onChange(resetFilters);
    }
  };
  
  // Стили
  const inputClassName = `${COMPONENT_STYLES.input.base} ${(disabled || isLoading || isApplying) ? 'opacity-60 cursor-not-allowed' : ''}`;
  const labelClassName = `block ${TEXT_STYLES.small}`;
  const buttonBaseClass = `${COMPONENT_STYLES.button.base} text-sm px-4 py-1`;
  
  // Определяем, есть ли изменения в фильтрах
  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);
  
  // Проверка активных фильтров
  const hasActiveFilters = Object.entries(localFilters).some(([key, value]) => {
    if (key === 'status' || key === 'platform' || key === 'paymentStatus') return value !== 'all';
    return value !== '' && value !== undefined && value !== null;
  });
  
  // Состояние блокировки UI
  const isBlocked = disabled || isLoading || isApplying;
  
  // Проверяем точное совпадение диапазонов
  const isExactRange = (from: string, to: string) => {
    return from && to && from === to;
  };
  
  // Получаем опции статусов, платежей и платформ
  const statusOptions = getOrderStatusOptions(t);
  const paymentStatusOptions = getPaymentStatusOptions(t);
  const platformOptions = getSalesChannelOptions(t);
  
  return (
    <Card padding="md" className="bg-background">
      <form onSubmit={(e) => { e.preventDefault(); handleApplyFilters(); }}>
        <Grid cols={2} gap="md" className="mb-4">
          {/* 1-й ряд: Диапазон дат и Сумма заказа */}
          
          {/* Диапазон дат */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.date_range', { defaultValue: "Период" })}
            </label>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="date"
                  placeholder={t('filters.from', { defaultValue: "От" })}
                  className={inputClassName}
                  value={localFilters.dateFrom}
                  onChange={(e) => handleLocalChange('dateFrom', e.target.value)}
                  disabled={isBlocked}
                />
                <input
                  type="date"
                  placeholder={t('filters.to', { defaultValue: "До" })}
                  className={inputClassName}
                  value={localFilters.dateTo}
                  onChange={(e) => handleLocalChange('dateTo', e.target.value)}
                  disabled={isBlocked}
                />
              </div>
            </div>
          </div>

          {/* Сумма заказа */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.amount_range', { defaultValue: "Сумма" })}
            </label>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder={t('filters.from', { defaultValue: "От" })}
                  className={`${inputClassName} ${isExactRange(localFilters.amountFrom, localFilters.amountTo) ? 'border-primary' : ''}`}
                  value={localFilters.amountFrom}
                  onChange={(e) => handleLocalChange('amountFrom', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                  step="0.01"
                />
                <input
                  type="number"
                  placeholder={t('filters.to', { defaultValue: "До" })}
                  className={`${inputClassName} ${isExactRange(localFilters.amountFrom, localFilters.amountTo) ? 'border-primary' : ''}`}
                  value={localFilters.amountTo}
                  onChange={(e) => handleLocalChange('amountTo', e.target.value)}
                  disabled={isBlocked}
                  min="0"
                  step="0.01"
                />
              </div>
              
              {/* Кнопка точного совпадения */}
              {localFilters.amountFrom && !isExactRange(localFilters.amountFrom, localFilters.amountTo) && (
                <button
                  type="button"
                  onClick={() => setExactMatch('amountFrom')}
                  className="text-xs text-primary hover:underline self-start"
                  disabled={isBlocked}
                >
                  {t('filters.set_exact_match', { defaultValue: "Точное совпадение" })}
                </button>
              )}
            
              {isExactRange(localFilters.amountFrom, localFilters.amountTo) && (
                <div className="text-xs text-primary mt-1">
                  {t('filters.exact_match', { defaultValue: "Точное совпадение" })}
                </div>
              )}
            </div>
          </div>
        </Grid>
        
        <Grid cols={3} gap="md">
          {/* 2-й ряд: Статус заказа, Канал продаж/Платформа, Статус оплаты */}
          
          {/* Статус заказа */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.order_status', { defaultValue: "Статус заказа" })}
            </label>
            <select
              value={localFilters.status}
              onChange={(e) => handleLocalChange('status', e.target.value)}
              className={`${inputClassName} ${localFilters.status !== 'all' ? 'border-primary' : ''}`}
              disabled={isBlocked}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Платформа/Канал продаж */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.platform', { defaultValue: "Канал продаж" })}
            </label>
            <select
              value={localFilters.platform}
              onChange={(e) => handleLocalChange('platform', e.target.value)}
              className={`${inputClassName} ${localFilters.platform !== 'all' ? 'border-primary' : ''}`}
              disabled={isBlocked}
            >
              {platformOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Статус оплаты */}
          <div className="space-y-2">
            <label className={labelClassName}>
              {t('filters.payment_status', { defaultValue: "Статус оплаты" })}
            </label>
            <select
              value={localFilters.paymentStatus}
              onChange={(e) => handleLocalChange('paymentStatus', e.target.value)}
              className={`${inputClassName} ${localFilters.paymentStatus !== 'all' ? 'border-primary' : ''}`}
              disabled={isBlocked}
            >
              {paymentStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </Grid>
        
        <Flex justify="end" gap="sm" className="mt-4">
          {/* Кнопка сброса */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleReset}
              className={`${buttonBaseClass} ${COMPONENT_STYLES.button.secondary}`}
              disabled={isBlocked}
            >
              {t('filters.reset', { defaultValue: "Сбросить" })}
            </button>
          )}
          
          {/* Кнопка применения фильтров */}
          <button
            type="submit"
            className={`${buttonBaseClass} ${COMPONENT_STYLES.button.primary} ${isApplying ? 'animate-pulse' : ''}`}
            disabled={isBlocked || (!hasChanges && !isApplying)}
          >
            {isLoading || isApplying ? (
              <Flex align="center" gap="xs">
                <Loader className="animate-spin h-4 w-4" />
                {t('common:loading', { defaultValue: "Загрузка..." })}
              </Flex>
            ) : (
              t('filters.apply', { defaultValue: "Применить" })
            )}
          </button>
        </Flex>
      </form>
      
      {/* Индикатор загрузки */}
      {(isLoading || isApplying) && (
        <div className="mt-2 text-center text-xs text-secondary">
          <span className="inline-block mr-1">
            <svg className="animate-spin h-3 w-3 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          {t('filters.loading', { defaultValue: "Загрузка..." })}
        </div>
      )}
    </Card>
  );
};

export default React.memo(Filters);